import type { ProductView, UnitPrice } from "./types";
import { isProductImagePath } from "./product-image-path";

export interface SharedProductCatalogSnapshot {
  id: string;
  chain: string;
  title: string;
  valid_from: string;
  valid_until: string;
}

export interface SharedProductSnapshot {
  id: string;
  catalog_id: string;
  page: number;
  store: string;
  product_ro: string;
  product_ru: string;
  product_uk: string;
  brand: string | null;
  weight_or_volume: string | null;
  old_price_mdl: number | null;
  new_price_mdl: number | null;
  required_card?: "casa_mega_digital";
  discount_percent: number | null;
  unit_price: UnitPrice | null;
  note_ro: string | null;
  note_ru: string | null;
  note_uk: string | null;
  bounding_box: [number, number, number, number];
  image: string;
  share_image: string;
  product_id?: string;
  category_id?: string;
  comparison_group_id?: string;
  catalog: SharedProductCatalogSnapshot;
}

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const IDENTIFIER_PATTERN = /^[a-z\d_-]+$/i;
// Full verified offer conditions can exceed 500 characters. Preserve them in
// the snapshot; the route still bounds the complete request to 32 KB.
const MAX_OFFER_NOTE_LENGTH = 2_000;

function boundedString(value: unknown, maximum: number): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= maximum;
}

function optionalString(value: unknown, maximum: number): value is string | null {
  return value === null || (typeof value === "string" && value.length <= maximum);
}

function optionalPrice(value: unknown): value is number | null {
  return (
    value === null ||
    (typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1_000_000)
  );
}

function validUnitPrice(value: unknown): value is UnitPrice | null {
  if (value === null) {
    return true;
  }
  if (!value || typeof value !== "object") {
    return false;
  }
  const candidate = value as Partial<UnitPrice>;
  return (
    (candidate.basis === "kg" ||
      candidate.basis === "l" ||
      candidate.basis === "10pcs" ||
      candidate.basis === "100pcs" ||
      candidate.basis === "100sheets") &&
    typeof candidate.minimum_mdl === "number" &&
    Number.isFinite(candidate.minimum_mdl) &&
    candidate.minimum_mdl >= 0 &&
    candidate.minimum_mdl <= 1_000_000 &&
    typeof candidate.maximum_mdl === "number" &&
    Number.isFinite(candidate.maximum_mdl) &&
    candidate.maximum_mdl >= candidate.minimum_mdl &&
    candidate.maximum_mdl <= 1_000_000 &&
    typeof candidate.is_range === "boolean"
  );
}

function validBoundingBox(value: unknown): value is [number, number, number, number] {
  return (
    Array.isArray(value) &&
    value.length === 4 &&
    value.every(
      (coordinate) =>
        Number.isInteger(coordinate) && coordinate >= 0 && coordinate <= 1000,
    ) &&
    value[0] < value[2] &&
    value[1] < value[3]
  );
}

function optionalIdentifier(value: unknown): value is string | undefined {
  return (
    value === undefined ||
    (typeof value === "string" && value.length <= 128 && IDENTIFIER_PATTERN.test(value))
  );
}

export function normalizeSharedProductSnapshot(
  value: unknown,
): SharedProductSnapshot | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const candidate = value as Partial<SharedProductSnapshot>;
  const catalog = candidate.catalog as Partial<SharedProductCatalogSnapshot> | undefined;
  if (
    !boundedString(candidate.id, 128) ||
    !IDENTIFIER_PATTERN.test(candidate.id) ||
    !boundedString(candidate.catalog_id, 128) ||
    !IDENTIFIER_PATTERN.test(candidate.catalog_id) ||
    typeof candidate.page !== "number" ||
    !Number.isInteger(candidate.page) ||
    candidate.page < 1 ||
    candidate.page > 2_000 ||
    !boundedString(candidate.store, 80) ||
    !boundedString(candidate.product_ro, 240) ||
    typeof candidate.product_ru !== "string" ||
    candidate.product_ru.length > 240 ||
    !(
      candidate.product_uk === undefined ||
      (typeof candidate.product_uk === "string" && candidate.product_uk.length <= 240)
    ) ||
    !optionalString(candidate.brand, 120) ||
    !optionalString(candidate.weight_or_volume, 80) ||
    !optionalPrice(candidate.old_price_mdl) ||
    !optionalPrice(candidate.new_price_mdl) ||
    !(
      candidate.discount_percent === null ||
      (typeof candidate.discount_percent === "number" &&
        Number.isInteger(candidate.discount_percent) &&
        candidate.discount_percent >= 0 &&
        candidate.discount_percent <= 100)
    ) ||
    !(candidate.required_card === undefined || candidate.required_card === "casa_mega_digital") ||
    !validUnitPrice(candidate.unit_price) ||
    !optionalString(candidate.note_ro, MAX_OFFER_NOTE_LENGTH) ||
    !optionalString(candidate.note_ru, MAX_OFFER_NOTE_LENGTH) ||
    !(
      candidate.note_uk === undefined ||
      optionalString(candidate.note_uk, MAX_OFFER_NOTE_LENGTH)
    ) ||
    !validBoundingBox(candidate.bounding_box) ||
    !isProductImagePath(candidate.image, "webp") ||
    !isProductImagePath(candidate.share_image, "jpg") ||
    !optionalIdentifier(candidate.product_id) ||
    !optionalIdentifier(candidate.category_id) ||
    !optionalIdentifier(candidate.comparison_group_id) ||
    !catalog ||
    !boundedString(catalog.id, 128) ||
    catalog.id !== candidate.catalog_id ||
    !boundedString(catalog.chain, 80) ||
    !boundedString(catalog.title, 160) ||
    typeof catalog.valid_from !== "string" ||
    !DATE_PATTERN.test(catalog.valid_from) ||
    typeof catalog.valid_until !== "string" ||
    !DATE_PATTERN.test(catalog.valid_until)
  ) {
    return null;
  }
  return {
    id: candidate.id,
    catalog_id: candidate.catalog_id,
    page: candidate.page,
    store: candidate.store,
    product_ro: candidate.product_ro,
    product_ru: candidate.product_ru,
    product_uk: candidate.product_uk?.trim() || candidate.product_ru || candidate.product_ro,
    brand: candidate.brand,
    weight_or_volume: candidate.weight_or_volume,
    old_price_mdl: candidate.old_price_mdl,
    new_price_mdl: candidate.new_price_mdl,
    ...(candidate.required_card ? { required_card: candidate.required_card } : {}),
    discount_percent: candidate.discount_percent,
    unit_price: candidate.unit_price,
    note_ro: candidate.note_ro,
    note_ru: candidate.note_ru,
    note_uk: candidate.note_uk ?? candidate.note_ru ?? candidate.note_ro,
    bounding_box: candidate.bounding_box,
    image: candidate.image,
    share_image: candidate.share_image,
    ...(candidate.product_id ? { product_id: candidate.product_id } : {}),
    ...(candidate.category_id ? { category_id: candidate.category_id } : {}),
    ...(candidate.comparison_group_id
      ? { comparison_group_id: candidate.comparison_group_id }
      : {}),
    catalog: {
      id: catalog.id,
      chain: catalog.chain,
      title: catalog.title,
      valid_from: catalog.valid_from,
      valid_until: catalog.valid_until,
    },
  };
}

export function createSharedProductSnapshot(
  product: ProductView,
): SharedProductSnapshot {
  return {
    id: product.id,
    catalog_id: product.catalog_id,
    page: product.page,
    store: product.store,
    product_ro: product.product_ro,
    product_ru: product.product_ru,
    product_uk: product.product_uk,
    brand: product.brand,
    weight_or_volume: product.weight_or_volume,
    old_price_mdl: product.old_price_mdl,
    new_price_mdl: product.new_price_mdl,
    ...(product.required_card ? { required_card: product.required_card } : {}),
    discount_percent: product.discount_percent,
    unit_price: product.unit_price,
    note_ro: product.note_ro,
    note_ru: product.note_ru,
    note_uk: product.note_uk,
    bounding_box: product.bounding_box,
    image: product.image,
    share_image: product.share_image,
    ...(product.product_id ? { product_id: product.product_id } : {}),
    ...(product.category_id ? { category_id: product.category_id } : {}),
    ...(product.comparison_group_id
      ? { comparison_group_id: product.comparison_group_id }
      : {}),
    catalog: {
      id: product.catalog.id,
      chain: product.catalog.chain,
      title: product.catalog.title,
      valid_from: product.catalog.valid_from,
      valid_until: product.catalog.valid_until,
    },
  };
}

export function productViewFromSharedSnapshot(
  snapshot: SharedProductSnapshot,
): ProductView {
  const { catalog, ...product } = snapshot;
  return {
    ...product,
    image: snapshot.share_image,
    catalog: {
      ...catalog,
      pages: Math.max(1, snapshot.page),
      file: "",
      run: "shared",
      recognized_pages: 0,
      completed_pages: 0,
      product_count: 1,
      page_counts: [{ page: snapshot.page, products: 1 }],
      products: [],
    },
  };
}

export function serializeSharedProductSnapshot(
  snapshot: SharedProductSnapshot,
): string {
  return JSON.stringify({ version: 1, kind: "product", product: snapshot });
}

export function parseStoredSharedProductSnapshot(
  payload: string,
): SharedProductSnapshot | null {
  try {
    const value = JSON.parse(payload) as {
      version?: unknown;
      kind?: unknown;
      product?: unknown;
    };
    if (value.version !== 1 || value.kind !== "product") {
      return null;
    }
    return normalizeSharedProductSnapshot(value.product);
  } catch {
    return null;
  }
}
